/** Machine-readable error codes returned by the API. Clients switch on these, not on messages. */
export const ApiErrorCode = {
  VALIDATION_FAILED: 'VALIDATION_FAILED',
  UNAUTHENTICATED: 'UNAUTHENTICATED',
  INVALID_CREDENTIALS: 'INVALID_CREDENTIALS',
  TOKEN_EXPIRED: 'TOKEN_EXPIRED',
  TOKEN_INVALID: 'TOKEN_INVALID',
  FORBIDDEN: 'FORBIDDEN',
  PERMISSION_DENIED: 'PERMISSION_DENIED',
  TENANT_CONTEXT_MISSING: 'TENANT_CONTEXT_MISSING',
  TENANT_INACTIVE: 'TENANT_INACTIVE',
  BRANCH_ACCESS_DENIED: 'BRANCH_ACCESS_DENIED',
  BRANCH_CONTEXT_MISSING: 'BRANCH_CONTEXT_MISSING',
  NOT_FOUND: 'NOT_FOUND',
  CONFLICT: 'CONFLICT',
  RATE_LIMITED: 'RATE_LIMITED',
  /** A report export would exceed the row limit. Nothing is truncated: the caller narrows the filters and asks again. */
  EXPORT_TOO_LARGE: 'EXPORT_TOO_LARGE',
  /** A photograph upload is larger than the configured limit. */
  MEDIA_TOO_LARGE: 'MEDIA_TOO_LARGE',
  /** A photograph upload is not a JPEG, PNG or WebP image, whatever its name or declared type says. */
  MEDIA_UNSUPPORTED_TYPE: 'MEDIA_UNSUPPORTED_TYPE',
  /** A label was asked for a variant with no active barcode; generate one first. The details name the variant. */
  VARIANT_HAS_NO_BARCODE: 'VARIANT_HAS_NO_BARCODE',

  /** A mutating endpoint was called without the Idempotency-Key header it requires. */
  IDEMPOTENCY_KEY_REQUIRED: 'IDEMPOTENCY_KEY_REQUIRED',
  /** The key has been seen before, against a different operation, body, user or branch. */
  IDEMPOTENCY_KEY_REUSED: 'IDEMPOTENCY_KEY_REUSED',
  /** An identical request is already running. Retry after the interval in the header. */
  REQUEST_IN_PROGRESS: 'REQUEST_IN_PROGRESS',
  /**
   * A sale line's recommendation cannot be confirmed: its selection token failed a check, or the seller may not sell with one. Each refused line is named in the details. Never cached, because removing the recommendation, rotating a secret or granting a permission changes the answer.
   */
  RECOMMENDATION_PROVENANCE_INVALID: 'RECOMMENDATION_PROVENANCE_INVALID',
  /** An order that may hold only one invoice already has one. The details name it. */
  INVOICE_ALREADY_ISSUED: 'INVOICE_ALREADY_ISSUED',
  /** The generic order cancel refuses an order that holds an optical job; it is cancelled through the optical cancellation, which reverses its stock and money together. */
  OPTICAL_ORDER_REQUIRES_OPTICAL_CANCELLATION: 'OPTICAL_ORDER_REQUIRES_OPTICAL_CANCELLATION',
  /** The request named an older version of the optical job than the one stored. Re-read it and try again. */
  OPTICAL_JOB_VERSION_CONFLICT: 'OPTICAL_JOB_VERSION_CONFLICT',
  /** An optical job moves one step forward at a time; the requested step is not the next one. */
  OPTICAL_JOB_TRANSITION_NOT_ALLOWED: 'OPTICAL_JOB_TRANSITION_NOT_ALLOWED',
  /** The optical job has been sent to the lab, so its prescription, lab and fitting heights are fixed. */
  OPTICAL_JOB_NOT_EDITABLE: 'OPTICAL_JOB_NOT_EDITABLE',
  /** Something the lab needs is missing: an active lab, the prescription, or a fitting height. The details name it. */
  OPTICAL_JOB_NOT_READY_FOR_LAB: 'OPTICAL_JOB_NOT_READY_FOR_LAB',
  /** A pair cannot be handed over while the order still owes money that no active payment plan covers. */
  OPTICAL_BALANCE_OUTSTANDING: 'OPTICAL_BALANCE_OUTSTANDING',
  /** A pair's prescription is outside what its lens can be made in, or does not suit its design. The details name the eye and value. */
  PRESCRIPTION_OUT_OF_LENS_RANGE: 'PRESCRIPTION_OUT_OF_LENS_RANGE',
  /** The chosen clinical prescription cannot be used for a new pair: not issued, expired, replaced, voided, or missing an eye or a PD. The details say which. */
  CLINICAL_PRESCRIPTION_NOT_USABLE: 'CLINICAL_PRESCRIPTION_NOT_USABLE',
  /** The job's clinical prescription was replaced, voided or has expired since it was chosen, so the job cannot go to the lab. Choose another prescription while the job is ORDERED. */
  CLINICAL_PRESCRIPTION_NO_LONGER_USABLE: 'CLINICAL_PRESCRIPTION_NO_LONGER_USABLE',
  /** The line belongs to an optical job taken at the counter, which moves only through its own job actions. */
  OPTICAL_JOB_REQUIRES_JOB_WORKFLOW: 'OPTICAL_JOB_REQUIRES_JOB_WORKFLOW',
  /** The job was created from records that predate optical jobs; it continues through the order's existing workflow. */
  LEGACY_OPTICAL_JOB: 'LEGACY_OPTICAL_JOB',
  /** The optical order has been cancelled: it cannot be cancelled again, and no payment, invoice or optical step is recorded against it any more. */
  OPTICAL_ORDER_CANCELLED: 'OPTICAL_ORDER_CANCELLED',
  /** The optical order cannot be cancelled as a whole: it is not confirmed, holds no optical job, has handed something over, holds a line outside its jobs, or has a return or credit note. The details name each reason. */
  OPTICAL_CANCELLATION_NOT_ALLOWED: 'OPTICAL_CANCELLATION_NOT_ALLOWED',
  /** A job of the order has been sent to the lab or gone further, so the order can no longer be cancelled: every job must still be ORDERED. The details name each job. */
  OPTICAL_CANCELLATION_AFTER_LAB: 'OPTICAL_CANCELLATION_AFTER_LAB',
  /** The order holds more than one issued invoice, so which one a cancellation offsets must be settled by hand. The details name them. */
  OPTICAL_CANCELLATION_MULTIPLE_INVOICES: 'OPTICAL_CANCELLATION_MULTIPLE_INVOICES',
  /** The order's payments, refunds or stock issues do not add up, so it cannot be reversed automatically. The details name what disagrees. */
  OPTICAL_CANCELLATION_RECORDS_INCONSISTENT: 'OPTICAL_CANCELLATION_RECORDS_INCONSISTENT',
  /** A refund the cancellation needs could not be recorded, so nothing was cancelled. */
  OPTICAL_CANCELLATION_REFUND_FAILED: 'OPTICAL_CANCELLATION_REFUND_FAILED',
  /** The order's payment plan could not be closed, so nothing was cancelled. */
  OPTICAL_CANCELLATION_PLAN_NOT_CLOSED: 'OPTICAL_CANCELLATION_PLAN_NOT_CLOSED',
  /** The credit note offsetting the order's invoice could not be issued, so nothing was cancelled. */
  OPTICAL_CANCELLATION_CREDIT_NOTE_FAILED: 'OPTICAL_CANCELLATION_CREDIT_NOTE_FAILED',
  /** A stock issue of the order could not be reversed, so nothing was cancelled. */
  OPTICAL_CANCELLATION_STOCK_REVERSAL_FAILED: 'OPTICAL_CANCELLATION_STOCK_REVERSAL_FAILED',
  /** A later payment is taken at the branch the order belongs to; no branch collects another's balance. */
  PAYMENT_BRANCH_MISMATCH: 'PAYMENT_BRANCH_MISMATCH',
  /** Glasses are handed over, and an optical order is cancelled with its refunds, at the branch the order belongs to; no branch hands over another's glasses or pays back another's takings. */
  OPTICAL_BRANCH_MISMATCH: 'OPTICAL_BRANCH_MISMATCH',
  /** The request named an older version of the transfer than the one stored. Re-read it and try again. */
  TRANSFER_VERSION_CONFLICT: 'TRANSFER_VERSION_CONFLICT',
  /** A receipt would take a transfer line past the quantity dispatched on it. The details name the line and what is still outstanding. */
  TRANSFER_RECEIPT_EXCEEDS_DISPATCHED: 'TRANSFER_RECEIPT_EXCEEDS_DISPATCHED',
  /** The lot number already exists for this variant with a different expiry date. Leave the date out to use the recorded one, or use another lot number. */
  LOT_EXPIRY_MISMATCH: 'LOT_EXPIRY_MISMATCH',
  /** The lot number already exists for this variant with a different manufactured date. Leave the date out to use the recorded one, or use another lot number. */
  LOT_MANUFACTURED_DATE_MISMATCH: 'LOT_MANUFACTURED_DATE_MISMATCH',
  /** The lot's printed expiry date is before today's business date, so it cannot be received. */
  LOT_EXPIRED_ON_ARRIVAL: 'LOT_EXPIRED_ON_ARRIVAL',
  /** The request named an older version of the supplier than the one stored. Re-read it and try again. */
  SUPPLIER_VERSION_CONFLICT: 'SUPPLIER_VERSION_CONFLICT',
  /** The supplier is already inactive. */
  SUPPLIER_ALREADY_INACTIVE: 'SUPPLIER_ALREADY_INACTIVE',
  /** The supplier is already active. */
  SUPPLIER_ALREADY_ACTIVE: 'SUPPLIER_ALREADY_ACTIVE',

  // Purchasing
  PURCHASE_ORDER_VERSION_CONFLICT: 'PURCHASE_ORDER_VERSION_CONFLICT',
  PURCHASE_ORDER_NOT_DRAFT: 'PURCHASE_ORDER_NOT_DRAFT',
  PURCHASE_ORDER_NOT_EDITABLE: 'PURCHASE_ORDER_NOT_EDITABLE',
  PURCHASE_ORDER_EMPTY: 'PURCHASE_ORDER_EMPTY',
  PURCHASE_ORDER_BRANCH_MISMATCH: 'PURCHASE_ORDER_BRANCH_MISMATCH',
  PURCHASE_ORDER_ALREADY_CANCELLED: 'PURCHASE_ORDER_ALREADY_CANCELLED',
  PURCHASE_ORDER_CANNOT_CANCEL: 'PURCHASE_ORDER_CANNOT_CANCEL',
  PURCHASE_ORDER_HAS_RECEIPTS: 'PURCHASE_ORDER_HAS_RECEIPTS',
  PURCHASE_ORDER_SUPPLIER_INACTIVE: 'PURCHASE_ORDER_SUPPLIER_INACTIVE',
  PURCHASE_ORDER_ITEM_INACTIVE: 'PURCHASE_ORDER_ITEM_INACTIVE',
  PURCHASE_ORDER_ITEM_MADE_TO_ORDER: 'PURCHASE_ORDER_ITEM_MADE_TO_ORDER',
  PURCHASE_ORDER_DUPLICATE_ITEM: 'PURCHASE_ORDER_DUPLICATE_ITEM',
  PURCHASE_ORDER_CLOSE_NOT_ALLOWED: 'PURCHASE_ORDER_CLOSE_NOT_ALLOWED',
  PURCHASE_RECEIPT_PO_NOT_RECEIVABLE: 'PURCHASE_RECEIPT_PO_NOT_RECEIVABLE',
  PURCHASE_RECEIPT_VERSION_CONFLICT: 'PURCHASE_RECEIPT_VERSION_CONFLICT',
  PURCHASE_INVOICE_NUMBER_ALREADY_USED: 'PURCHASE_INVOICE_NUMBER_ALREADY_USED',
  PURCHASE_INVOICE_OVER_INVOICE: 'PURCHASE_INVOICE_OVER_INVOICE',
  PURCHASE_INVOICE_MIXED_SUPPLIER: 'PURCHASE_INVOICE_MIXED_SUPPLIER',
  PURCHASE_INVOICE_MIXED_BRANCH: 'PURCHASE_INVOICE_MIXED_BRANCH',
  PURCHASE_INVOICE_MIXED_CURRENCY: 'PURCHASE_INVOICE_MIXED_CURRENCY',
  PURCHASE_INVOICE_DUPLICATE_LINE: 'PURCHASE_INVOICE_DUPLICATE_LINE',
  PURCHASE_INVOICE_DATE_IN_FUTURE: 'PURCHASE_INVOICE_DATE_IN_FUTURE',
  PURCHASE_INVOICE_ALREADY_VOIDED: 'PURCHASE_INVOICE_ALREADY_VOIDED',

  // Payables (Phase 4.2): money leaving the business against a posted supplier invoice.
  PAYABLE_OVER_ALLOCATION: 'PAYABLE_OVER_ALLOCATION',
  SUPPLIER_PAYMENT_ALREADY_VOIDED: 'SUPPLIER_PAYMENT_ALREADY_VOIDED',
  SUPPLIER_PAYMENT_MIXED_SUPPLIER: 'SUPPLIER_PAYMENT_MIXED_SUPPLIER',
  SUPPLIER_PAYMENT_MIXED_BRANCH: 'SUPPLIER_PAYMENT_MIXED_BRANCH',
  SUPPLIER_PAYMENT_MIXED_CURRENCY: 'SUPPLIER_PAYMENT_MIXED_CURRENCY',
  SUPPLIER_PAYMENT_DUPLICATE_INVOICE: 'SUPPLIER_PAYMENT_DUPLICATE_INVOICE',
  SUPPLIER_PAYMENT_INVOICE_NOT_POSTED: 'SUPPLIER_PAYMENT_INVOICE_NOT_POSTED',
  SUPPLIER_PAYMENT_DATE_OUT_OF_RANGE: 'SUPPLIER_PAYMENT_DATE_OUT_OF_RANGE',
  SUPPLIER_PAYMENT_METHOD_NOT_ALLOWED: 'SUPPLIER_PAYMENT_METHOD_NOT_ALLOWED',
  SUPPLIER_PAYMENT_REFERENCE_REQUIRED: 'SUPPLIER_PAYMENT_REFERENCE_REQUIRED',
  PURCHASE_INVOICE_HAS_PAYMENTS: 'PURCHASE_INVOICE_HAS_PAYMENTS',

  // Expenses (Phase 4.4): direct-paid operating costs. Nothing here is a payable.
  EXPENSE_CATEGORY_INACTIVE: 'EXPENSE_CATEGORY_INACTIVE',
  EXPENSE_CATEGORY_VERSION_CONFLICT: 'EXPENSE_CATEGORY_VERSION_CONFLICT',
  EXPENSE_CATEGORY_ALREADY_ACTIVE: 'EXPENSE_CATEGORY_ALREADY_ACTIVE',
  EXPENSE_CATEGORY_ALREADY_INACTIVE: 'EXPENSE_CATEGORY_ALREADY_INACTIVE',
  EXPENSE_DATE_IN_FUTURE: 'EXPENSE_DATE_IN_FUTURE',
  EXPENSE_METHOD_NOT_ALLOWED: 'EXPENSE_METHOD_NOT_ALLOWED',
  EXPENSE_REFERENCE_REQUIRED: 'EXPENSE_REFERENCE_REQUIRED',
  EXPENSE_ALREADY_VOIDED: 'EXPENSE_ALREADY_VOIDED',
  PURCHASE_RECEIPT_OVER_RECEIVE: 'PURCHASE_RECEIPT_OVER_RECEIVE',
  PURCHASE_RECEIPT_LOT_REQUIRED: 'PURCHASE_RECEIPT_LOT_REQUIRED',
  PURCHASE_RECEIPT_LOT_NOT_ALLOWED: 'PURCHASE_RECEIPT_LOT_NOT_ALLOWED',
  PURCHASE_RECEIPT_MTO_NOT_ALLOWED: 'PURCHASE_RECEIPT_MTO_NOT_ALLOWED',
  // CRM (Phase 5.3): messaging policy, templates, communication history and branch identity.
  /** The request named an older version of the customer's preferences than the one stored. Re-read and try again. */
  CONTACT_PREFERENCE_VERSION_CONFLICT: 'CONTACT_PREFERENCE_VERSION_CONFLICT',
  COMMUNICATION_NOT_FOUND: 'COMMUNICATION_NOT_FOUND',
  COMMUNICATION_TEMPLATE_NOT_FOUND: 'COMMUNICATION_TEMPLATE_NOT_FOUND',
  /** A template out of use cannot render a new message. What it already rendered is untouched. */
  COMMUNICATION_TEMPLATE_INACTIVE: 'COMMUNICATION_TEMPLATE_INACTIVE',
  COMMUNICATION_TEMPLATE_VERSION_CONFLICT: 'COMMUNICATION_TEMPLATE_VERSION_CONFLICT',
  COMMUNICATION_TEMPLATE_CODE_TAKEN: 'COMMUNICATION_TEMPLATE_CODE_TAKEN',
  /** A body named a placeholder its purpose does not offer. The allow-list is per purpose and closed. */
  COMMUNICATION_TEMPLATE_INVALID_VARIABLE: 'COMMUNICATION_TEMPLATE_INVALID_VARIABLE',
  /** The customer's own messaging policy refuses this message: operational suppressed, or marketing without consent. */
  COMMUNICATION_POLICY_BLOCKED: 'COMMUNICATION_POLICY_BLOCKED',
  // Communication automation (Phase 5.5).
  COMMUNICATION_AUTOMATION_VERSION_CONFLICT: 'COMMUNICATION_AUTOMATION_VERSION_CONFLICT',
  /** The chosen template cannot drive this trigger: withdrawn, wrong channel, or a different purpose. */
  COMMUNICATION_AUTOMATION_TEMPLATE_INVALID: 'COMMUNICATION_AUTOMATION_TEMPLATE_INVALID',
  REMINDER_NOT_FOUND: 'REMINDER_NOT_FOUND',
  // Reports & Analytics (Phase 6).
  /** The request named an older version of the reporting settings than the one stored. Re-read and try again. */
  REPORTING_SETTINGS_VERSION_CONFLICT: 'REPORTING_SETTINGS_VERSION_CONFLICT',
  PRINT_TEMPLATE_VERSION_CONFLICT: 'PRINT_TEMPLATE_VERSION_CONFLICT',
  PRINT_TEMPLATE_INVALID: 'PRINT_TEMPLATE_INVALID',
  BUSINESS_PROFILE_VERSION_CONFLICT: 'BUSINESS_PROFILE_VERSION_CONFLICT',
  BUSINESS_PROFILE_LOGO_CONFLICT: 'BUSINESS_PROFILE_LOGO_CONFLICT',
  REMINDER_ALREADY_COMPLETED: 'REMINDER_ALREADY_COMPLETED',
  REMINDER_ALREADY_CANCELLED: 'REMINDER_ALREADY_CANCELLED',
  /** The tenant has not said which country its numbers belong to, so no international destination can be derived without guessing. */
  PHONE_ROUTING_NOT_CONFIGURED: 'PHONE_ROUTING_NOT_CONFIGURED',
  INVALID_PHONE: 'INVALID_PHONE',
  WHATSAPP_CONFIG_NOT_FOUND: 'WHATSAPP_CONFIG_NOT_FOUND',
  WHATSAPP_CONFIG_VERSION_CONFLICT: 'WHATSAPP_CONFIG_VERSION_CONFLICT',
  /** A branch cannot be pointed at an alternative identity it has not configured. */
  WHATSAPP_IDENTITY_NOT_CONFIGURED: 'WHATSAPP_IDENTITY_NOT_CONFIGURED',
  /** Both identities were the same number, which leaves "which one sent this" unanswerable. */
  WHATSAPP_IDENTITY_DUPLICATE: 'WHATSAPP_IDENTITY_DUPLICATE',

  // WhatsApp sessions (Phase 5.4).
  /** The server has no key to encrypt session credentials with, so it refuses to hold them at all. */
  WHATSAPP_ENCRYPTION_UNAVAILABLE: 'WHATSAPP_ENCRYPTION_UNAVAILABLE',
  /** Another server process currently owns this session's socket. */
  WHATSAPP_SESSION_BUSY: 'WHATSAPP_SESSION_BUSY',
  WHATSAPP_SESSION_NOT_FOUND: 'WHATSAPP_SESSION_NOT_FOUND',
  /** The session's stored authentication was sealed under a retired key version and has been discarded; the number must be paired again. */
  WHATSAPP_REAUTH_REQUIRED: 'WHATSAPP_REAUTH_REQUIRED',
  /** An identity cannot become the one a branch sends from until it is actually connected. */
  WHATSAPP_SESSION_NOT_CONNECTED: 'WHATSAPP_SESSION_NOT_CONNECTED',

  INTERNAL_ERROR: 'INTERNAL_ERROR',
  SERVICE_UNAVAILABLE: 'SERVICE_UNAVAILABLE',
} as const;
export type ApiErrorCode = (typeof ApiErrorCode)[keyof typeof ApiErrorCode];

export interface ApiResponseMeta {
  requestId: string;
  timestamp: string;
}

export interface ApiSuccessResponse<T> {
  success: true;
  data: T;
  meta: ApiResponseMeta;
}

export interface ApiErrorDetail {
  field?: string;
  message: string;
}

export interface ApiErrorResponse {
  success: false;
  error: {
    code: ApiErrorCode | string;
    message: string;
    details?: ApiErrorDetail[];
  };
  meta: ApiResponseMeta & { path: string };
}

export type ApiResponse<T> = ApiSuccessResponse<T> | ApiErrorResponse;

export interface PaginationMeta {
  page: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

export interface PaginatedResult<T> {
  items: T[];
  pagination: PaginationMeta;
}

export const DEFAULT_PAGE_SIZE = 25;
export const MAX_PAGE_SIZE = 100;

/** Header used by the client to declare which branch it is acting on. Always re-validated server-side. */
export const BRANCH_HEADER = 'x-branch-id';
/** Correlation id echoed back on every response and attached to every log line. */
export const REQUEST_ID_HEADER = 'x-request-id';

/**
 * Errors an idempotent retry may safely be answered with from cache.
 *
 * Deliberately an allowlist of specific codes rather than a status range. A 401 must be
 * re-evaluated because the caller's session may have changed; a 429 must be re-evaluated because
 * the window has moved; a 500 must not be cached at all because the business transaction did not
 * commit. Only a decision the server would deterministically reach again belongs here.
 */
export const CACHEABLE_ERROR_CODES: readonly ApiErrorCode[] = [
  ApiErrorCode.VALIDATION_FAILED,
  ApiErrorCode.CONFLICT,
];

export function isCacheableErrorCode(code: string): boolean {
  return (CACHEABLE_ERROR_CODES as readonly string[]).includes(code);
}

/** Where a replayed response comes from. */
export const IdempotencyReplayStrategy = {
  /**
   * Re-read the committed entity and return its current representation. Preferred: it stores no
   * copy of the response, so clinical and personal data never lands in infrastructure tables.
   */
  RECONSTRUCT: 'RECONSTRUCT',
  /** Store a small, explicitly whitelisted body. Only for responses that carry no personal data. */
  SNAPSHOT: 'SNAPSHOT',
} as const;
export type IdempotencyReplayStrategy =
  (typeof IdempotencyReplayStrategy)[keyof typeof IdempotencyReplayStrategy];

export const IDEMPOTENCY_KEY_HEADER = 'idempotency-key';

/**
 * Required on every capture-device request.
 *
 * A custom header cannot be sent cross-site without a CORS preflight, so its presence proves the
 * request came from a page allowed to send it — the capture page — rather than from a form or an
 * image tag on some other site riding the capture cookie.
 */
export const CAPTURE_REQUEST_HEADER = 'x-optiflow-capture';

/**
 * Every custom header the browser client sends, and therefore every one CORS has to admit.
 *
 * One list, because the two ends of it live in different packages. A header added to the client
 * but missing from the API's `allowedHeaders` fails only in a browser — the preflight succeeds and
 * the real request is blocked — so supertest, which speaks to the app in-process and never runs a
 * preflight, passes every e2e while the counter is unusable.
 */
export const CLIENT_REQUEST_HEADERS: readonly string[] = [
  'Content-Type',
  'Authorization',
  BRANCH_HEADER,
  REQUEST_ID_HEADER,
  IDEMPOTENCY_KEY_HEADER,
  CAPTURE_REQUEST_HEADER,
];
